import { Button, Input, Modal } from "components";
import styles from "./styles.module.scss";
import { useApiRequest, useToast } from "hooks";
import { candidateBasicFitRequestData, candidateBasicFitService } from "api";
import { useMemo, useState } from "react";
import { openFullScreen, handleAssessmentTimer, getError } from "helpers";
import { useParams } from "react-router-dom";
import { AssessmentProgress } from "pages";

interface AbiaEssentialInfoProps {
  show: boolean;
  close: () => void;
  handleContinue: (metric: string) => void;
  candidate: {
    firstName: string;
    lastName: string;
    email: string;
    dob: string;
    gender: string;
    phone: string;
  };
  resume: (data: AssessmentProgress) => void;
}

const AbiaEssentialInfoUI: React.FC<AbiaEssentialInfoProps> = ({
  show,
  close,
  handleContinue,
  candidate,
  resume,
}) => {
  // State
  const [startTime, setStartTime] = useState<number>(0);
  const { id: assessmentID } = useParams();

  // Hooks
  const { openToast } = useToast();

  // API Request Hooks
  const { run, data: response, requestStatus, error } = useApiRequest({});

  const submit = async () => {
    openFullScreen();
    const local_candidate = JSON.parse(
      localStorage.getItem("candidate") ?? "{}"
    );

    if (!assessmentID) return;

    const submitData: candidateBasicFitRequestData = {
      full_name: candidate.firstName + " " + candidate.lastName,
      phone_number: candidate.phone,
      candidate: local_candidate.id,
      assessment: assessmentID,
    };

    // Set start time
    setStartTime(new Date().getTime());
    run(candidateBasicFitService(submitData));
  };

  useMemo(() => {
    if (response?.status === 201) {
      const data = response.data.data;
      handleAssessmentTimer(startTime);

      localStorage.setItem("assessmentId", `${data.id}`);

      const answered = parseInt(localStorage.getItem("answered") ?? "0");
      localStorage.setItem("answered", `${answered + 2}`);

      handleContinue("basic_expectations");
    } else if (error) {
      if (error.response?.data?.data?.status === "started") {
        const data = error.response?.data?.data;
        localStorage.setItem("assessmentId", `${data.id}`);
        openToast({
          show: true,
          text: "Looks like you've already started this assessment. Restoring your progress in 3..2..1",
          heading: "Uh-oh",
          type: "error",
          timeOut: 2000,
        });
        setTimeout(() => {
          resume({
            basic_expectations: true,
            work_motivators: data.progress.work_motivators ?? false,
            work_qualities: data.progress.work_qualities ?? false,
            work_style: data.progress.work_style ?? false,
            personality: data.progress.personality ?? false,
            soft_skill: data.progress.soft_skill ?? false,
            domain_knowledge: data.progress.domain_knowledge ?? false,
          });
        }, 2000);
      } else {
        handleAssessmentTimer(startTime);
        openToast({
          show: true,
          ...getError({
            error,
            text: "Failed to submit, please try again or contact support",
            heading: "Sorry",
          }),
          type: "error",
          timeOut: 5000,
        });
      }
    }
  }, [response, error]);

  const showLoader = requestStatus.isPending;

  return (
    <>
      <Modal
        contentClassName={styles.modal}
        show={show}
        close={close}
        position={"centered"}
      >
        <h1 className={styles.ttl}>Your Registration Details</h1>
        <p className={styles.txt}>
          You have been registered to take this assessment. Review your
          information and click the button to proceed.
        </p>
        <div className={styles.info}>
          <div className={styles.info__item}>
            <p>First Name:</p>
            <p>{candidate.firstName}</p>
          </div>
          <div className={styles.info__item}>
            <p>Last Name:</p>
            <p>{candidate.lastName}</p>
          </div>
          <div className={styles.info__item}>
            <p>Email:</p>
            <p>{candidate.email}</p>
          </div>
          <div className={styles.info__item}>
            <p>Phone number:</p>
            <p>{candidate.phone}</p>
          </div>
          <div className={styles.info__item}>
            <p>Gender:</p>
            <p>{candidate.gender}</p>
          </div>
          <div className={styles.info__item}>
            <p>Date of birth:</p>
            <p>{candidate.dob}</p>
          </div>
        </div>
        <Button className={styles.btn} type={"fill"} onClick={submit}>
          Continue
        </Button>
      </Modal>
    </>
  );
};

export { AbiaEssentialInfoUI };
